// pageHistoryStorage.js

const STORAGE_KEY = 'tarot_page_history';
const MAX_HISTORY_COUNT = 50;
const MAX_AGE_DAYS = 30;

/**
 * 유효한 히스토리만 반환하고 자동으로 정리 (1개월 이내)
 * @returns {Array} 유효한 히스토리 배열
 */
const getValidHistories = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];

        const histories = JSON.parse(stored);
        const oneMonthAgo = Date.now() - (MAX_AGE_DAYS * 24 * 60 * 60 * 1000);

        // 1개월 이내의 데이터만 필터링
        const validHistories = histories.filter(h => h.timestamp > oneMonthAgo);

        // 필터링 결과가 원본과 다르면 localStorage 업데이트
        if (validHistories.length !== histories.length) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(validHistories));
        }

        return validHistories;
    } catch (error) {
        console.error('Failed to parse histories:', error);
        return [];
    }
};

/**
 * 페이지 히스토리 저장
 * @param {string} hashId - 타로 결과의 hash_id
 * @param {number} currentIndex - 현재 페이지 인덱스
 * @param {string} historyId - history_id (옵션)
 */
export const savePageHistory = (hashId, currentIndex, historyId = null) => {
    try {
        // 기존 히스토리 가져오기 (자동 정리됨)
        const histories = getValidHistories();

        // 동일한 hashId가 있으면 업데이트, 없으면 추가
        const existingIndex = histories.findIndex(h => h.hashId === hashId);

        const newEntry = {
            hashId,
            historyId,
            currentIndex,
            timestamp: Date.now()
        };

        if (existingIndex !== -1) {
            // 기존 항목 업데이트
            histories[existingIndex] = newEntry;
        } else {
            // 새 항목 추가
            histories.push(newEntry);
        }

        // 최신순으로 정렬
        histories.sort((a, b) => b.timestamp - a.timestamp);

        // 50개 제한
        const limitedHistories = histories.slice(0, MAX_HISTORY_COUNT);

        localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedHistories));
    } catch (error) {
        console.error('Failed to save page history:', error);
    }
};

/**
 * 특정 hashId의 마지막 페이지 인덱스 조회
 * @param {string} hashId - 타로 결과의 hash_id
 * @returns {number|null} - 저장된 페이지 인덱스 또는 null
 */
export const getLastPageIndex = (hashId) => {
    try {
        const histories = getValidHistories();
        const history = histories.find(h => h.hashId === hashId);
        return history ? history.currentIndex : null;
    } catch (error) {
        console.error('Failed to get last page index:', error);
        return null;
    }
};

/**
 * 저장된 히스토리 개수 확인 (디버깅/통계용)
 * @returns {number} 유효한 히스토리 개수
 */
export const getHistoryCount = () => {
    try {
        const histories = getValidHistories();
        return histories.length;
    } catch (error) {
        console.error('Failed to get history count:', error);
        return 0;
    }
};

/**
 * 모든 히스토리 삭제 (수동 초기화용)
 */
export const clearAllHistories = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error('Failed to clear histories:', error);
    }
};



// Guide 숨김 관련 상수
const GUIDE_HIDDEN_KEY = 'tarot_guide_hidden';
const GUIDE_HIDE_DURATION_DAYS = 30; // 1개월

/**
 * Guide 숨김 설정 저장
 * @param {string} guideType - 가이드 타입 (예: 'extra_question')
 */
export const hideGuideForMonth = (guideType = 'extra_question') => {
    try {
        const hiddenGuides = getHiddenGuides();

        hiddenGuides[guideType] = {
            hiddenAt: Date.now(),
            expiresAt: Date.now() + (GUIDE_HIDE_DURATION_DAYS * 24 * 60 * 60 * 1000)
        };

        localStorage.setItem(GUIDE_HIDDEN_KEY, JSON.stringify(hiddenGuides));
    } catch (error) {
        console.error('Failed to hide guide:', error);
    }
};

/**
 * 숨겨진 가이드 목록 조회 및 만료된 항목 정리
 * @returns {Object} 유효한 숨김 설정 객체
 */
const getHiddenGuides = () => {
    try {
        const stored = localStorage.getItem(GUIDE_HIDDEN_KEY);
        if (!stored) return {};

        const hiddenGuides = JSON.parse(stored);
        const now = Date.now();
        let hasExpired = false;

        // 만료된 항목 제거
        Object.keys(hiddenGuides).forEach(key => {
            if (hiddenGuides[key].expiresAt < now) {
                delete hiddenGuides[key];
                hasExpired = true;
            }
        });

        // 만료된 항목이 있으면 localStorage 업데이트
        if (hasExpired) {
            localStorage.setItem(GUIDE_HIDDEN_KEY, JSON.stringify(hiddenGuides));
        }

        return hiddenGuides;
    } catch (error) {
        console.error('Failed to get hidden guides:', error);
        return {};
    }
};

/**
 * 특정 가이드가 숨김 상태인지 확인
 * @param {string} guideType - 가이드 타입
 * @returns {boolean} 숨김 여부
 */
export const isGuideHidden = (guideType = 'extra_question') => {
    try {
        const hiddenGuides = getHiddenGuides();
        return !!hiddenGuides[guideType];
    } catch (error) {
        console.error('Failed to check guide hidden status:', error);
        return false;
    }
};

/**
 * 특정 가이드의 숨김 해제
 * @param {string} guideType - 가이드 타입
 */
export const showGuide = (guideType = 'extra_question') => {
    try {
        const hiddenGuides = getHiddenGuides();
        delete hiddenGuides[guideType];
        localStorage.setItem(GUIDE_HIDDEN_KEY, JSON.stringify(hiddenGuides));
    } catch (error) {
        console.error('Failed to show guide:', error);
    }
};

/**
 * 모든 가이드 숨김 해제
 */
export const clearAllHiddenGuides = () => {
    try {
        localStorage.removeItem(GUIDE_HIDDEN_KEY);
    } catch (error) {
        console.error('Failed to clear hidden guides:', error);
    }
};



// 심층풀이 읽음 상태 저장 키 생성
const getExtraQuestionReadKey = (historyId) => `tarot_extra_read_${historyId}`;

// 심층풀이 읽음 상태 초기화
export const initializeExtraQuestionReadStatus = (historyId, tarotBoxHistory) => {
    const key = getExtraQuestionReadKey(historyId);
    let readStatus = {};

    try {
        const saved = localStorage.getItem(key);
        if (saved) {
            readStatus = JSON.parse(saved);
        }

        // 새로운 심층풀이 확인 및 추가
        // 1. 최초 심층풀이
        if (tarotBoxHistory.extra_question && tarotBoxHistory.extra_answer) {
            const extraId = `initial_${tarotBoxHistory.extra_question_page_index}`;
            if (!readStatus[extraId]) {
                readStatus[extraId] = {
                    id: extraId,
                    pageIndex: tarotBoxHistory.extra_question_page_index,
                    isRead: false,
                    createdAt: tarotBoxHistory.updated_at
                };
            }
        }

        // 2. 추가 심층풀이들
        if (tarotBoxHistory.extra_question_histories) {
            tarotBoxHistory.extra_question_histories.forEach(item => {
                const extraId = `extra_${item.id}`;
                if (!readStatus[extraId]) {
                    readStatus[extraId] = {
                        id: extraId,
                        originalId: item.id,
                        pageIndex: item.page_index,
                        isRead: false,
                        createdAt: item.created_at
                    };
                }
            });
        }

        localStorage.setItem(key, JSON.stringify(readStatus));
        return readStatus;
    } catch (error) {
        console.error('Failed to initialize extra question read status:', error);
        return {};
    }
};

// 특정 페이지의 심층풀이 읽음 처리
export const markExtraQuestionAsRead = (historyId, pageIndex) => {
    const key = getExtraQuestionReadKey(historyId);

    try {
        const saved = localStorage.getItem(key);
        if (!saved) return false;

        const readStatus = JSON.parse(saved);
        let hasChanges = false;

        // 해당 페이지의 모든 심층풀이를 읽음 처리
        Object.keys(readStatus).forEach(extraId => {
            if (readStatus[extraId].pageIndex === pageIndex && !readStatus[extraId].isRead) {
                readStatus[extraId].isRead = true;
                readStatus[extraId].readAt = new Date().toISOString();
                hasChanges = true;
            }
        });

        if (hasChanges) {
            console.log(`Marking extra questions on page ${pageIndex} as read for history ${historyId}`);
            localStorage.setItem(key, JSON.stringify(readStatus));
        }

        return hasChanges;
    } catch (error) {
        console.error('Failed to mark extra question as read:', error);
        return false;
    }
};

// 모든 심층풀이가 읽혔는지 확인
export const areAllExtraQuestionsRead = (historyId) => {
    const key = getExtraQuestionReadKey(historyId);

    try {
        const saved = localStorage.getItem(key);
        if (!saved) return true;

        const readStatus = JSON.parse(saved);
        return Object.values(readStatus).every(item => item.isRead);
    } catch (error) {
        console.error('Failed to check extra questions read status:', error);
        return true;
    }
};

// 특정 페이지에 읽지 않은 심층풀이가 있는지 확인
export const hasUnreadExtraQuestion = (historyId, pageIndex) => {
    const key = getExtraQuestionReadKey(historyId);

    try {
        const saved = localStorage.getItem(key);
        if (!saved) return false;

        const readStatus = JSON.parse(saved);
        return Object.values(readStatus).some(
            item => item.pageIndex === pageIndex && !item.isRead
        );
    } catch (error) {
        console.error('Failed to check unread extra question:', error);
        return false;
    }
};

// 읽음 상태 리셋 (디버깅용)
export const resetExtraQuestionReadStatus = (historyId) => {
    const key = getExtraQuestionReadKey(historyId);
    localStorage.removeItem(key);
};